import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, FileDown, Copy, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableColumnVisibilityMenu } from '@/components/shared/TableColumnVisibilityMenu'
import { ListLoader } from '@/components/shared/ListLoader'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'
import { overTimeRequestApiService } from '@/services/overTimeRequestApiService'
import type { OverTimeRequestApiRow, OverTimeStatusLookupItem } from '@/api/types'

type ColumnKey = 'date' | 'timeFrom' | 'timeTo' | 'totalHours' | 'status' | 'action'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'date', labelKey: 'overtimeRequestPage.colOvertimeDate' },
  { key: 'timeFrom', labelKey: 'overtimeRequestPage.colTimeFrom' },
  { key: 'timeTo', labelKey: 'overtimeRequestPage.colTimeTo' },
  { key: 'totalHours', labelKey: 'overtimeRequestPage.colTotalHours' },
  { key: 'status', labelKey: 'overtimeRequestPage.colStatus' },
  { key: 'action', labelKey: 'overtimeRequestPage.colAction' },
]

function formatDate(dateOnly: string, locale: string) {
  if (!dateOnly) return '—'
  const d = new Date(`${dateOnly}T12:00:00`)
  if (Number.isNaN(d.getTime())) return String(dateOnly).slice(0, 10)
  return d.toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function csvEscape(cell: string | number) {
  return `"${String(cell).replace(/"/g, '""')}"`
}

function calcHours(dateOnly: string, from: string, to: string): string {
  try {
    if (!dateOnly || !from || !to) return '—'
    const start = new Date(`${dateOnly}T${from}:00`)
    let end = new Date(`${dateOnly}T${to}:00`)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—'
    // If timeTo is earlier, assume next day (overnight OT)
    if (end.getTime() < start.getTime()) {
      end = new Date(end.getTime() + 24 * 60 * 60 * 1000)
    }
    const diffMs = end.getTime() - start.getTime()
    const hours = diffMs / (1000 * 60 * 60)
    if (!Number.isFinite(hours) || hours < 0) return '—'
    const rounded = Math.round(hours * 100) / 100
    return String(rounded)
  } catch {
    return '—'
  }
}

export const OvertimeRequestList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const locale = i18n.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en'
  const { user } = useAuthStore()
  const branchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<OverTimeRequestApiRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusLookups, setStatusLookups] = useState<OverTimeStatusLookupItem[]>([])
  /** API uses codeType id; 0 = All */
  const [codeType, setCodeType] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    date: true,
    timeFrom: true,
    timeTo: true,
    totalHours: true,
    status: true,
    action: true,
  })

  const columnMenuItems = useMemo(
    () => ALL_COLUMNS.filter((c) => c.key !== 'action').map((c) => ({ id: c.key, label: t(c.labelKey) })),
    [t],
  )

  const toggleColumn = (id: string) => {
    const key = id as ColumnKey
    if (!ALL_COLUMNS.some((c) => c.key === key)) return
    if (key === 'action') return
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  useEffect(() => {
    let cancelled = false
    void overTimeRequestApiService
      .getStatusLookups()
      .then((list) => {
        if (!cancelled) setStatusLookups(list || [])
      })
      .catch(() => {
        if (!cancelled) setStatusLookups([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const statusNameById = useMemo(() => {
    const map: Record<number, string> = {}
    for (const s of statusLookups || []) {
      const id = Number(s.lookupCodeId ?? s.id ?? 0)
      if (!id) continue
      const name = String(s.lookupNameEn ?? s.lookupNameAr ?? s.nameEn ?? s.nameAr ?? s.name ?? '').trim()
      if (name) map[id] = name
    }
    return map
  }, [statusLookups])

  const statusText = (statusId: number): string => {
    // Normalize to match other request lists (Pending/Approved/Rejected) based on status id
    if (statusId === 434) return t('overtimeRequestPage.statusPending', { defaultValue: 'Pending' })
    if (statusId === 435) return t('overtimeRequestPage.statusApproved', { defaultValue: 'Approved' })
    if (statusId === 436) return t('overtimeRequestPage.statusRejected', { defaultValue: 'Rejected' })
    return statusNameById[statusId] ?? (statusId ? String(statusId) : '—')
  }

  const isApprovedRow = (row: OverTimeRequestApiRow) => {
    const s = Number(row.status ?? 0)
    return s === 435
  }

  const load = async () => {
    if (!branchId) {
      setRows([])
      setTotalCount(0)
      return
    }
    setLoading(true)
    try {
      const res = await overTimeRequestApiService.getAll(branchId, pageNumber, pageSize, codeType)
      const list = res.data || []
      const q = searchQuery.trim().toLowerCase()
      const filtered = q
        ? list.filter((r) =>
            [
              String(r.overTimeDate ?? ''),
              String(r.timeFrom ?? ''),
              String(r.timeTo ?? ''),
              calcHours(String(r.overTimeDate ?? '').slice(0, 10), String(r.timeFrom ?? '').slice(11, 16), String(r.timeTo ?? '').slice(11, 16)),
              statusText(Number(r.status ?? 0)),
              String(r.details ?? ''),
            ].some((v) => String(v).toLowerCase().includes(q)),
          )
        : list
      setRows(filtered)
      setTotalCount(res.totalRecords || 0)
    } catch (e: any) {
      const msg = e?.message ?? t('overtimeRequestPage.fetchFailed')
      setRows([])
      setTotalCount(0)
      const isNoData = /not found|no data|no records/i.test(String(msg))
      if (!isNoData) toast.error(String(msg))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [branchId, searchQuery, codeType, pageNumber, pageSize])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const page = Math.min(pageNumber, totalPages)
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const to = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount)

  const statusBadge = (row: OverTimeRequestApiRow) => {
    const s = Number(row.status ?? 0)
    const text = statusText(s)
    return (
      <Badge variant={s === 435 ? 'default' : s === 434 ? 'secondary' : 'outline'} className="text-xs">
        {text}
      </Badge>
    )
  }

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm(undefined, t('overtimeRequestPage.deleteConfirm'))
    if (!confirmed) return
    try {
      await overTimeRequestApiService.delete(id)
      await showSuccess(t('overtimeRequestPage.deletedTitle'), t('overtimeRequestPage.deletedMessage'))
      await load()
    } catch (e: any) {
      await showError(t('overtimeRequestPage.deleteFailedTitle'), e?.message ?? t('overtimeRequestPage.deleteFailedMessage'))
    }
  }

  const handleCopy = async () => {
    if (totalCount === 0) {
      toast.error(t('overtimeRequestPage.nothingToCopy'))
      return
    }
    if (!branchId) return
    const res = await overTimeRequestApiService.getAll(branchId, 1, 10000, codeType)
    const header = ALL_COLUMNS.filter((c) => c.key !== 'action' && visibleColumns[c.key]).map((c) => t(c.labelKey))
    const lines = (res.data || []).map((r) => {
      const cells: (string | number)[] = []
      if (visibleColumns.date) cells.push(String(r.overTimeDate ?? '').slice(0, 10))
      if (visibleColumns.timeFrom) cells.push(String(r.timeFrom ?? '').slice(11, 16) || '—')
      if (visibleColumns.timeTo) cells.push(String(r.timeTo ?? '').slice(11, 16) || '—')
      if (visibleColumns.totalHours) cells.push(calcHours(String(r.overTimeDate ?? '').slice(0, 10), String(r.timeFrom ?? '').slice(11, 16), String(r.timeTo ?? '').slice(11, 16)))
      if (visibleColumns.status) cells.push(statusText(Number(r.status ?? 0)))
      return cells.join('\t')
    })
    await navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('overtimeRequestPage.copied'))
  }

  const handleExportCSV = () => {
    if (totalCount === 0) {
      toast.error(t('overtimeRequestPage.nothingToExport'))
      return
    }
    if (!branchId) return
    void overTimeRequestApiService.getAll(branchId, 1, 10000, codeType).then((res) => {
      const header = ALL_COLUMNS.filter((c) => c.key !== 'action' && visibleColumns[c.key])
        .map((c) => csvEscape(t(c.labelKey)))
        .join(',')
      const body = (res.data || [])
        .map((r) => {
          const cells: (string | number)[] = []
          if (visibleColumns.date) cells.push(String(r.overTimeDate ?? '').slice(0, 10))
          if (visibleColumns.timeFrom) cells.push(String(r.timeFrom ?? '').slice(11, 16) || '—')
          if (visibleColumns.timeTo) cells.push(String(r.timeTo ?? '').slice(11, 16) || '—')
          if (visibleColumns.totalHours) cells.push(calcHours(String(r.overTimeDate ?? '').slice(0, 10), String(r.timeFrom ?? '').slice(11, 16), String(r.timeTo ?? '').slice(11, 16)))
          if (visibleColumns.status) cells.push(statusText(Number(r.status ?? 0)))
          return cells.map(csvEscape).join(',')
        })
        .join('\n')
      const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `overtime_requests_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success(t('overtimeRequestPage.exported'))
    })
  }

  const handlePrint = () => {
    window.print()
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('overtimeRequestPage.pageTitle')} description={t('overtimeRequestPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">{t('overtimeRequestPage.selectBranchFirst')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('overtimeRequestPage.pageTitle')}
        description={t('overtimeRequestPage.pageDescription')}
        action={{
          label: t('overtimeRequestPage.addRequest'),
          onClick: () => navigate('/self-services/overtime-requests/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('overtimeRequestPage.labelStatus')}</label>
                <Select
                  value={String(codeType)}
                  onValueChange={(v) => {
                    setCodeType(Number(v) || 0)
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder={t('overtimeRequestPage.placeholderAll')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('overtimeRequestPage.placeholderAll')}</SelectItem>
                    {statusLookups.map((s) => {
                      const id = Number(s.lookupCodeId ?? s.id ?? 0)
                      if (!id) return null
                      const label = String(s.lookupNameEn ?? s.lookupNameAr ?? s.nameEn ?? s.nameAr ?? s.name ?? id).trim()
                      return (
                        <SelectItem key={id} value={String(id)}>
                          {label}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('overtimeRequestPage.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-9"
                />
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                <TableColumnVisibilityMenu
                  columns={columnMenuItems}
                  visibility={visibleColumns}
                  onToggle={toggleColumn}
                  triggerLabel={t('overtimeRequestPage.columnVisibility')}
                  menuTitle={t('overtimeRequestPage.columnsToggle')}
                />
                <Button variant="outline" size="sm" onClick={handleExportCSV}>
                  <FileDown className="mr-2 h-4 w-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                  <Copy className="mr-2 h-4 w-4" />
                  {t('overtimeRequestPage.copy')}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="mr-2 h-4 w-4" />
                  {t('overtimeRequestPage.print')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      {visibleColumns.date && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('overtimeRequestPage.colOvertimeDate')}
                        </TableHead>
                      )}
                      {visibleColumns.timeFrom && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('overtimeRequestPage.colTimeFrom')}
                        </TableHead>
                      )}
                      {visibleColumns.timeTo && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('overtimeRequestPage.colTimeTo')}
                        </TableHead>
                      )}
                      {visibleColumns.totalHours && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('overtimeRequestPage.colTotalHours')}
                        </TableHead>
                      )}
                      {visibleColumns.status && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('overtimeRequestPage.colStatus')}
                        </TableHead>
                      )}
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary w-28">
                        {t('overtimeRequestPage.colAction')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const visibleDataCols = ALL_COLUMNS.filter((c) => c.key !== 'action').filter((c) => visibleColumns[c.key]).length
                      const colSpan = visibleDataCols + 1 // + action
                      if (rows.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={colSpan} className="text-center py-12 text-muted-foreground">
                              {t('overtimeRequestPage.noData')}
                            </TableCell>
                          </TableRow>
                        )
                      }
                      return rows.map((row) => {
                        const approved = isApprovedRow(row)
                        const canEdit = !approved && Number(row.isEditable ?? 1) !== 0
                        return (
                          <TableRow
                            key={row.id}
                            className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                          >
                            {visibleColumns.date && (
                              <TableCell className="px-4 py-2 text-sm text-center align-middle">
                                {formatDate(String(row.overTimeDate ?? '').slice(0, 10), locale)}
                              </TableCell>
                            )}
                            {visibleColumns.timeFrom && (
                              <TableCell className="px-4 py-2 text-sm text-center align-middle">{String(row.timeFrom ?? '').slice(11, 16) || '—'}</TableCell>
                            )}
                            {visibleColumns.timeTo && (
                              <TableCell className="px-4 py-2 text-sm text-center align-middle">{String(row.timeTo ?? '').slice(11, 16) || '—'}</TableCell>
                            )}
                            {visibleColumns.totalHours && (
                              <TableCell className="px-4 py-2 text-sm text-center align-middle">
                                {calcHours(String(row.overTimeDate ?? '').slice(0, 10), String(row.timeFrom ?? '').slice(11, 16), String(row.timeTo ?? '').slice(11, 16))}
                              </TableCell>
                            )}
                            {visibleColumns.status && (
                              <TableCell className="px-4 py-2 text-sm text-center align-middle">{statusBadge(row)}</TableCell>
                            )}
                            <TableCell className="px-4 py-2 text-center align-middle">
                              <div className="flex justify-center gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:pointer-events-none"
                                  onClick={() => navigate(`/self-services/overtime-requests/${row.id}`)}
                                  title={t('overtimeRequestPage.editTitle')}
                                  disabled={!canEdit}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                  onClick={() => void handleDelete(Number(row.id))}
                                  title={t('overtimeRequestPage.deleteTitle')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    })()}
                  </TableBody>
                </Table>
              </div>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('overtimeRequestPage.pageSize')}</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      setPageSize(Number(v))
                      setPageNumber(1)
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="text-sm text-muted-foreground">
                  {t('overtimeRequestPage.paginationShowing', { from, to, total: totalCount })}
                </div>

                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                    <ChevronLeft className="h-4 w-4" />
                    {t('overtimeRequestPage.previous')}
                  </Button>
                  <span className="text-sm">{t('overtimeRequestPage.pageOf', { page, totalPages })}</span>
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    {t('overtimeRequestPage.next')}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

